import { useEffect, useRef, useState } from 'react'

// How long a fetch has to keep running before it earns a place on screen, and how
// long the bar holds once it has one. Without them, `loadingStore.global` — which
// flips on and off for every in-flight request anywhere in the app, including
// each page a table's scroll-triggered fetch pulls in — drove the old bar on and
// off dozens of times during ordinary use. A quick request now never draws
// anything, and a burst of quick ones reads as one steady bar instead of a
// stutter.
export const SHOW_DELAY_MS = 160
export const MIN_VISIBLE_MS = 320

/**
 * Turns a raw, flickering `active` boolean into a debounced `visible` one:
 * showing is delayed by `showDelay`, and once shown it holds for at least
 * `minVisible` even if `active` goes false again in the meantime.
 *
 * Timed entirely off nested `setTimeout`s rather than `Date.now()`, so a hide
 * requested before the minimum has elapsed is *remembered* (`pendingHide`) and
 * applied the moment the minimum's own timer fires, instead of computing "how
 * long is left" from a wall clock — which would need its own mocking in tests
 * and would drift if the tab was ever backgrounded in between.
 */
export const useSteadyVisible = (active, { showDelay = SHOW_DELAY_MS, minVisible = MIN_VISIBLE_MS } = {}) => {
	const [visible, setVisible] = useState(false)
	const showTimer = useRef()
	const minVisibleTimer = useRef()
	const canHide = useRef(true)
	const pendingHide = useRef(false)

	useEffect(() => {
		if (active) {
			// Requesting again before the minimum elapsed cancels any hide that was
			// waiting on it — the bar stays up through the whole burst.
			pendingHide.current = false

			if (!visible && !showTimer.current) {
				showTimer.current = setTimeout(() => {
					showTimer.current = undefined
					canHide.current = false
					setVisible(true)

					minVisibleTimer.current = setTimeout(() => {
						minVisibleTimer.current = undefined
						canHide.current = true
						if (pendingHide.current) {
							pendingHide.current = false
							setVisible(false)
						}
					}, minVisible)
				}, showDelay)
			}
		} else {
			clearTimeout(showTimer.current)
			showTimer.current = undefined

			if (visible) {
				if (canHide.current) {
					setVisible(false)
				} else {
					pendingHide.current = true
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [active, showDelay, minVisible])

	useEffect(
		() => () => {
			clearTimeout(showTimer.current)
			clearTimeout(minVisibleTimer.current)
		},
		[]
	)

	return visible
}
